/**
 * Shared date-range filtering utilities used across dashboard pages so every
 * module (overview, customers, finance, hand loans, reports, consultancy,
 * expenses, payroll) sorts/aggregates on the same selected window.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { resolveWorkspaceAsOfDate, WORKSPACE_AS_OF_EVENT, workspaceDateToMs } from './workspaceAsOf';
import { FINANCIAL_YEAR_EVENT, selectedFinancialYearRange } from './financialYear';

export type DateRange = { start: Date | null; end: Date | null };

export type DateRangePreset =
  | 'all'
  | 'today'
  | 'this_week'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'this_year'
  | 'financial_year'
  | 'custom';

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

/** Calendar anchor for presets — follows navbar working date when set. */
export function workspaceReferenceDate(): Date {
  const ymd = resolveWorkspaceAsOfDate();
  const ms = workspaceDateToMs(ymd);
  return ms ? new Date(ms) : new Date();
}

/** Resolve a preset to a concrete {start,end} window (null = open-ended). */
export function rangeForPreset(preset: DateRangePreset, now = workspaceReferenceDate()): DateRange {
  switch (preset) {
    case 'today':
      return { start: startOfDay(now), end: endOfDay(now) };
    case 'this_week': {
      const day = now.getDay(); // 0 = Sun
      const diff = (day + 6) % 7; // days since Monday
      const monday = new Date(now);
      monday.setDate(now.getDate() - diff);
      return { start: startOfDay(monday), end: endOfDay(now) };
    }
    case 'this_month':
      return { start: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), end: endOfDay(now) };
    case 'last_month': {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: startOfDay(first), end: endOfDay(last) };
    }
    case 'this_quarter': {
      const q = Math.floor(now.getMonth() / 3);
      return { start: startOfDay(new Date(now.getFullYear(), q * 3, 1)), end: endOfDay(now) };
    }
    case 'this_year':
      return { start: startOfDay(new Date(now.getFullYear(), 0, 1)), end: endOfDay(now) };
    case 'financial_year':
      // Tenant-selected FY (Settings → Financial year), full window.
      return selectedFinancialYearRange();
    case 'all':
    case 'custom':
    default:
      return { start: null, end: null };
  }
}

/** Coerce many date shapes (ISO string, YYYY-MM, Date, ms) to a Date or null. */
export function toDate(value: unknown): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const s = String(value).trim();
  // YYYY-MM (month bucket) → first of month
  if (/^\d{4}-\d{2}$/.test(s)) {
    const [y, m] = s.split('-').map(Number);
    return new Date(y, m - 1, 1);
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** True when `value`'s date falls inside the (inclusive) range. Open ends pass. */
export function inRange(value: unknown, range: DateRange): boolean {
  if (!range.start && !range.end) return true;
  const d = toDate(value);
  if (!d) return false;
  const t = d.getTime();
  if (range.start && t < startOfDay(range.start).getTime()) return false;
  if (range.end && t > endOfDay(range.end).getTime()) return false;
  return true;
}

/** Filter an array by a date field selector. */
export function filterByRange<T>(items: T[], range: DateRange, getDate: (item: T) => unknown): T[] {
  if (!range.start && !range.end) return items;
  return items.filter((it) => inRange(getDate(it), range));
}

/** Human-readable label for headings / PDF meta. */
export function formatRangeLabel(range: DateRange): string {
  const fmt = (d: Date) =>
    d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  if (!range.start && !range.end) return 'All dates';
  if (range.start && range.end) return `${fmt(range.start)} – ${fmt(range.end)}`;
  if (range.start) return `From ${fmt(range.start)}`;
  if (range.end) return `Until ${fmt(range.end)}`;
  return 'All dates';
}

export type UseDateRangeOptions = {
  /**
   * When false, presets use wall-clock today (super-admin).
   * Default true — shop dashboards follow the workspace "as of" date.
   */
  useWorkspaceAsOf?: boolean;
};

/** Stateful hook: manages a date range + preset for a page. */
export function useDateRange(initial: DateRangePreset = 'all', options?: UseDateRangeOptions) {
  const useAsOf = options?.useWorkspaceAsOf !== false;
  const anchor = useCallback(
    () => (useAsOf ? workspaceReferenceDate() : new Date()),
    [useAsOf]
  );

  const [preset, setPreset] = useState<DateRangePreset>(initial);
  const [range, setRange] = useState<DateRange>(() => rangeForPreset(initial, anchor()));

  useEffect(() => {
    if (!useAsOf) return undefined;
    const sync = () => {
      setRange((r) => {
        if (preset === 'custom') return r;
        return rangeForPreset(preset, anchor());
      });
    };
    window.addEventListener(WORKSPACE_AS_OF_EVENT, sync);
    window.addEventListener(FINANCIAL_YEAR_EVENT, sync);
    return () => {
      window.removeEventListener(WORKSPACE_AS_OF_EVENT, sync);
      window.removeEventListener(FINANCIAL_YEAR_EVENT, sync);
    };
  }, [preset, useAsOf, anchor]);

  const api = useMemo(
    () => ({
      preset,
      range,
      label: formatRangeLabel(range),
      setPreset: (p: DateRangePreset) => {
        setPreset(p);
        if (p !== 'custom') setRange(rangeForPreset(p, anchor()));
      },
      setStart: (d: Date | null) => {
        setPreset('custom');
        setRange((r) => ({ ...r, start: d }));
      },
      setEnd: (d: Date | null) => {
        setPreset('custom');
        setRange((r) => ({ ...r, end: d }));
      },
      clear: () => {
        setPreset('all');
        setRange({ start: null, end: null });
      },
    }),
    [preset, range, anchor]
  );

  return api;
}
